import {
  arrayPartitionByPredicate,
  arrayFindMap,
  arrayFilterMap,
} from '@synapex/tools/utils';

type BranchPathSegment = string;
export type BranchPath = BranchPathSegment[];

type Leaf = BranchPathSegment;
type Fork = [root: BranchPathSegment, branches: Tree[]];
export type Tree = Leaf | Fork;

export const INDEX_LEAF = '/' satisfies Leaf;

const isFork = (tree: Tree): tree is Fork => Array.isArray(tree);

const parseFork = ([root, branches]: Fork) => ({
  root,
  branches,
});

const isTreeRoot = (tree: Tree, bs: BranchPathSegment) => {
  if (isFork(tree)) {
    const { root } = parseFork(tree);
    return root === bs;
  } else {
    return tree === bs;
  }
};

const getTopBranchPath = (tree: Tree): BranchPath => {
  if (isFork(tree)) {
    const { root, branches } = parseFork(tree);

    if (branches.length === 0) {
      return [root];
    } else {
      return [root, ...getTopBranchPath(branches[0])];
    }
  } else {
    return [tree];
  }
};

const makeTree = (branchPath: BranchPath): Tree =>
  branchPath.length === 1
    ? branchPath[0]
    : [branchPath[0], [makeTree(branchPath.slice(1))]];

//

const completeBranchPathSegment = (
  treesArray: Array<Tree>,
  pathSegment: BranchPathSegment,
): BranchPath | null => {
  return (
    arrayFindMap(
      treesArray,
      (tree) => isTreeRoot(tree, pathSegment),
      (tree) => {
        if (isFork(tree)) {
          const { root, branches } = parseFork(tree);

          if (branches.length === 0) {
            return [root];
          } else {
            if (branches.includes(INDEX_LEAF)) {
              return [root];
            } else {
              return [root, ...getTopBranchPath(branches[0])];
            }
          }
        } else {
          return [tree];
        }
      },
    ) ?? null
  );
};

export const completeBranchPath = (
  treesArray: Array<Tree>,
  path: BranchPath,
): BranchPath | null => {
  if (path.length === 0) {
    if (treesArray.length === 0) {
      return null;
    } else {
      return getTopBranchPath(treesArray[0]);
    }
  } else if (path.length === 1) {
    return completeBranchPathSegment(treesArray, path[0]);
  }

  //path.length >= 2

  const [pathRoot, ...restPath] = path;

  //restPath.length >= 1

  return (
    arrayFindMap(
      treesArray,
      (tree) => isTreeRoot(tree, pathRoot),
      (tree) => {
        if (isFork(tree)) {
          const { root, branches } = parseFork(tree);

          const tail = completeBranchPath(branches, restPath);

          if (tail) {
            return [root, ...tail];
          } else {
            //no completion
            return null;
          }
        } else {
          return [tree];
        }
      },
    ) ?? null
  );
};

//

const writeBranchPathSegment = (
  treesArray: Array<Tree>,
  pathSegment: BranchPathSegment,
) => {
  const partition = arrayPartitionByPredicate(treesArray, (tree) =>
    isTreeRoot(tree, pathSegment),
  );

  if (partition) {
    const { before, element: _, after } = partition;
    //cut off element branches if any
    return [pathSegment, ...before, ...after];
  } else {
    return [pathSegment, ...treesArray];
  }
};

export const writeBranchPath = (
  treesArray: Array<Tree>,
  path: BranchPath,
): Array<Tree> => {
  if (path.length === 1) {
    return writeBranchPathSegment(treesArray, path[0]);
  }

  //path.length >= 2

  const [pathRoot, ...restPath] = path;

  //restPath.length >= 1

  const partition = arrayPartitionByPredicate(treesArray, (tree) =>
    isTreeRoot(tree, pathRoot),
  );

  if (partition) {
    const { before, element, after } = partition;

    if (isFork(element)) {
      const { root, branches } = parseFork(element);

      //same root, write to branches
      return [[root, writeBranchPath(branches, restPath)], ...before, ...after];
    } else {
      //substitute leaf with tree
      return [makeTree(path), ...before, ...after];
    }
  } else {
    return [makeTree(path), ...treesArray];
  }
};

export const pathnameToBranchPath = (pathname: string): BranchPath => {
  return arrayFilterMap(pathname.split(/\/+/), (segment) =>
    segment.length > 0 ? segment : undefined,
  );
};
